import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';

export default function BuildingShowcase() {
  const [showcaseData, setShowcaseData] = useState({
    title: 'Mühendislik Harikası.',
    desc: 'Akıllı ev entegrasyonu, birinci sınıf ahşap işçiliği ve kusursuz akustik izolasyon mimarisi.',
    image1: '/premium-project-block.png'
  });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'pages', 'home'), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        
        const defaultShowcaseTitle = 'Mühendislik Harikası.';
        const defaultShowcaseDesc = 'Sadece estetik değil; akıllı ev entegrasyonu, birinci sınıf ahşap işçiliği, kusursuz akustik izolasyon mimarisi ve devasa fore kazık zemin güçlendirmeleriyle nesiller boyu ayakta kalacak anıtsal projeler geliştiriyoruz.';

        const isCorruptTitle = data.showcaseTitle === 'YENİ NESİL MİMARİ';
        const isCorruptDesc = data.showcaseDesc && data.showcaseDesc.includes('Modern yaşamın tüm gereksinimlerini karşılayan');

        let finalImage1 = data.showcaseImage1;
        // Aggressive cache override for all old assets
        if (!finalImage1 || finalImage1.includes('premium-project') || finalImage1.includes('masterpiece') || finalImage1 === '/hero-atlas.jpg') {
            finalImage1 = '/showcase-modern-1.png';
        }

        setShowcaseData({
          title: isCorruptTitle ? defaultShowcaseTitle : (data.showcaseTitle || defaultShowcaseTitle),
          desc: isCorruptDesc ? defaultShowcaseDesc : (data.showcaseDesc || defaultShowcaseDesc),
          image1: finalImage1
        });
      }
    });
    return () => unsub();
  }, []);

  return (
    <section className="relative py-20 md:py-32 bg-zinc-900 overflow-hidden">
      {/* Premium Architectural Grid Background for Dark Mode */}
      <div 
        className="absolute inset-0 z-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '4rem 4rem'
        }}
      />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        
        {/* Başlık */}
        <div className="text-center mb-16 md:mb-24 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-red-500 font-bold uppercase tracking-widest text-xs md:text-sm mb-3 md:mb-4">Mimari Analiz</div>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight" style={{ fontFamily: 'Raleway, sans-serif' }}>
              {showcaseData.title}
            </h2>
            <div className="w-12 md:w-16 h-1 bg-red-600 mx-auto mt-6 md:mt-8 rounded-full shadow-[0_0_15px_rgba(220,38,38,0.5)]"></div>
            
            <p className="max-w-2xl mx-auto mt-6 text-zinc-400 font-medium">
              {showcaseData.desc}
            </p>
          </motion.div>
        </div>

        {/* Ana İçerik */}
        <div className="relative flex flex-col md:flex-row items-center justify-between gap-12 lg:gap-8 mt-12">
          
          {/* Sol Özellikler */}
          <div className="w-full md:w-1/3 space-y-6 md:space-y-8 z-20 order-2 md:order-1 px-4 lg:px-0">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-zinc-800 border border-zinc-700 p-6 md:p-8 rounded-3xl shadow-2xl text-left hover:-translate-y-2 hover:border-red-500/30 hover:shadow-[0_10px_30px_rgba(220,38,38,0.1)] transition-all duration-300"
            >
               <div className="w-12 h-12 md:w-14 md:h-14 bg-red-600/20 rounded-full flex items-center justify-center mb-4 md:mb-6 text-red-500 backdrop-blur-md">
                 <i className="ri-layout-masonry-line text-xl md:text-2xl"></i>
               </div>
               <h4 className="font-bold text-white mb-2 md:mb-3 text-lg md:text-xl tracking-tight">A-Sınıfı Detaylar</h4>
               <p className="text-zinc-400 text-sm md:text-base leading-relaxed">Akıllı ev entegrasyonu, birinci sınıf ahşap işçiliği ve kusursuz akustik izolasyon mimarisi.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 md:p-8 rounded-3xl shadow-2xl text-left hover:-translate-y-2 hover:border-red-500/30 hover:shadow-[0_10px_30px_rgba(220,38,38,0.1)] transition-all duration-300"
            >
               <div className="w-12 h-12 md:w-14 md:h-14 bg-white/10 border border-white/5 rounded-full flex items-center justify-center mb-4 md:mb-6 text-white">
                 <i className="ri-earth-line text-xl md:text-2xl"></i>
               </div>
               <h4 className="font-bold text-white mb-2 md:mb-3 text-lg md:text-xl tracking-tight">C50 & Fore Kazık</h4>
               <p className="text-zinc-400 text-sm md:text-base leading-relaxed">Deprem standartlarının çok ötesinde, devasa sismik temel güçlendirmeleri ve detaylı zemin etüdü.</p>
            </motion.div>
          </div>

          {/* Ortadaki Dev Bina */}
          <div className="w-full md:w-1/3 relative flex justify-center order-1 md:order-2">
            <div className="absolute inset-0 bg-red-600/15 rounded-full blur-[100px] opacity-100 z-0"></div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="relative z-10 w-full flex justify-center"
            >
              <motion.img 
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                src={showcaseData.image1} 
                alt="KARE YAPI İNŞAAT - Mühendislik Harikası" 
                className="w-[85%] md:w-full max-w-[320px] lg:max-w-[500px] h-auto object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] aspect-auto min-h-[400px]"
              />
            </motion.div>
          </div>

          {/* Sağ Özellikler */}
          <div className="w-full md:w-1/3 space-y-6 md:space-y-8 z-20 order-3 px-4 lg:px-0">
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 md:p-8 rounded-3xl shadow-2xl text-left hover:-translate-y-2 hover:border-red-500/30 hover:shadow-[0_10px_30px_rgba(220,38,38,0.1)] transition-all duration-300"
            >
               <div className="w-12 h-12 md:w-14 md:h-14 bg-white/10 border border-white/5 rounded-full flex items-center justify-center mb-4 md:mb-6 text-white">
                 <i className="ri-sun-line text-xl md:text-2xl"></i>
               </div>
               <h4 className="font-bold text-white mb-2 md:mb-3 text-lg md:text-xl tracking-tight">Gökyüzü Terasları</h4>
               <p className="text-zinc-400 text-sm md:text-base leading-relaxed">Şehri ayaklarınızın altına seren panoramik cam cepheler ve rüzgar testinden geçmiş ultra dayanıklı strüktür.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="bg-zinc-800 border border-zinc-700 p-6 md:p-8 rounded-3xl shadow-2xl text-left hover:-translate-y-2 hover:border-red-500/30 hover:shadow-[0_10px_30px_rgba(220,38,38,0.1)] transition-all duration-300"
            >
               <div className="w-12 h-12 md:w-14 md:h-14 bg-red-600/20 rounded-full flex items-center justify-center mb-4 md:mb-6 text-red-500 backdrop-blur-md">
                 <i className="ri-shield-check-line text-xl md:text-2xl"></i>
               </div>
               <h4 className="font-bold text-white mb-2 md:mb-3 text-lg md:text-xl tracking-tight">%100 Memnuniyet</h4>
               <p className="text-zinc-400 text-sm md:text-base leading-relaxed">Söz verdiğimiz gün ve tarihte eksiksiz anahtar teslim güvencesi ile satış sonrası tam destek garantisi.</p>
            </motion.div>
          </div>

        </div>

      </div>
    </section>
  );
}
